/**
 * Utility functions for formatting user display names consistently across the application.
 * This consolidates duplicate name formatting logic that was spread across multiple files.
 */

/**
 * User data shape that can be used for display name formatting
 */
export interface UserNameData {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/**
 * Formats a user's display name using a priority-based approach:
 * 1. Use displayName if it exists and is not just the email prefix
 * 2. Combine firstName and lastName if available
 * 3. Fall back to email prefix
 * 4. Final fallback to default value
 *
 * @param user - User data object with name fields
 * @param fallback - Optional fallback value if no name can be determined (default: 'Unknown User')
 * @returns Formatted display name
 *
 * @example
 * formatUserDisplayName({ displayName: 'John Doe', email: 'john@example.com' })
 * // Returns: 'John Doe'
 *
 * @example
 * formatUserDisplayName({ firstName: 'John', lastName: 'Doe', email: 'john@example.com' })
 * // Returns: 'John Doe'
 *
 * @example
 * formatUserDisplayName({ email: 'john@example.com' })
 * // Returns: 'john'
 */
export function formatUserDisplayName(
  user: UserNameData | null | undefined,
  fallback = 'Unknown User'
): string {
  if (!user) {
    return fallback;
  }

  // Check if displayName is valid (not empty and not just the email prefix)
  const emailPrefix = user.email?.split('@')[0];
  const hasValidDisplayName = user.displayName &&
    user.displayName.trim() !== '' &&
    user.displayName !== emailPrefix;

  if (hasValidDisplayName) {
    return user.displayName!.trim();
  }

  // Try combining first and last name
  const firstName = user.firstName?.trim() || '';
  const lastName = user.lastName?.trim() || '';

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  // Fall back to email prefix
  if (emailPrefix) {
    return emailPrefix;
  }

  return fallback;
}

/**
 * Formats a user's initials for avatar displays
 *
 * @param user - User data object with name fields
 * @returns Two-letter initials
 *
 * @example
 * formatUserInitials({ firstName: 'John', lastName: 'Doe' })
 * // Returns: 'JD'
 *
 * @example
 * formatUserInitials({ displayName: 'John Doe' })
 * // Returns: 'JD'
 *
 * @example
 * formatUserInitials({ email: 'john@example.com' })
 * // Returns: 'JO'
 */
export function formatUserInitials(
  user: UserNameData | null | undefined
): string {
  if (!user) {
    return '??';
  }

  const displayName = formatUserDisplayName(user, '');

  if (!displayName) {
    return '??';
  }

  const parts = displayName.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    const first = parts[0] ?? '';
    const last = parts[parts.length - 1] ?? '';
    if (!first || !last) return '??';
    return `${first[0]}${last[0]}`.toUpperCase();
  }

  if (parts.length === 1) {
    const [only] = parts;
    if (!only) return '??';
    if (only.length >= 2) {
      return only.substring(0, 2).toUpperCase();
    }
    const char = only[0];
    return char ? char.toUpperCase().repeat(2) : '??';
  }

  return '??';
}
